import { browserEffectRuntime } from "@crm/effect/client/runtime";
import { unsafeCurrentUtcIsoString } from "@crm/shared/time/beijing-time";
import { Data, Duration, Effect, Fiber } from "effect";
import { type Metric, onCLS, onFCP, onINP, onLCP, onTTFB } from "web-vitals";

type VitalEntry = {
  name: Metric["name"];
  value: number;
  rating: Metric["rating"];
  route: string;
  navigationType: string;
  recordedAt: string;
};

const FLUSH_INTERVAL_MS = 5000;
const MAX_BUFFER_SIZE = 10;
const ENDPOINT = "/api/web-vitals/collect";

let buffer: VitalEntry[] = [];
let scheduledFlush: Fiber.Fiber<void, never> | null = null;
let initialized = false;

class WebVitalsTransportError extends Data.TaggedError("WebVitalsTransportError")<{
  readonly cause: unknown;
}> {}

function getRoute(): string {
  return location.pathname;
}

function reportPayloadEffect(payload: string): Effect.Effect<void> {
  if (navigator.sendBeacon) {
    return Effect.try({
      try: () => {
        navigator.sendBeacon(ENDPOINT, new Blob([payload], { type: "application/json" }));
      },
      catch: (cause) => new WebVitalsTransportError({ cause }),
    }).pipe(Effect.ignore);
  }

  return Effect.tryPromise({
    try: () =>
      fetch(ENDPOINT, {
        method: "POST",
        body: payload,
        keepalive: true,
        headers: { "Content-Type": "application/json" },
      }),
    catch: (cause) => new WebVitalsTransportError({ cause }),
  }).pipe(Effect.asVoid, Effect.ignore);
}

function flushWebVitalsEffect(): Effect.Effect<void> {
  return Effect.sync(() => {
    if (buffer.length === 0) {
      return null;
    }

    const payload = JSON.stringify(buffer);
    buffer = [];
    return payload;
  }).pipe(
    Effect.flatMap((payload) => (payload === null ? Effect.void : reportPayloadEffect(payload))),
  );
}

function cancelScheduledFlush() {
  const fiber = scheduledFlush;
  scheduledFlush = null;

  if (fiber) {
    browserEffectRuntime.runFork(Fiber.interrupt(fiber).pipe(Effect.ignore));
  }
}

function flush() {
  cancelScheduledFlush();
  browserEffectRuntime.runFork(flushWebVitalsEffect().pipe(Effect.ignore));
}

function scheduleFlush() {
  if (scheduledFlush) {
    return;
  }

  scheduledFlush = browserEffectRuntime.runFork(
    Effect.sleep(Duration.millis(FLUSH_INTERVAL_MS)).pipe(
      Effect.tap(() =>
        Effect.sync(() => {
          scheduledFlush = null;
        }),
      ),
      Effect.andThen(flushWebVitalsEffect()),
      Effect.ignore,
    ),
  );
}

function onMetric(metric: Metric) {
  buffer.push({
    name: metric.name,
    value: metric.value,
    rating: metric.rating,
    route: getRoute(),
    navigationType: metric.navigationType,
    recordedAt: unsafeCurrentUtcIsoString(),
  });

  if (buffer.length >= MAX_BUFFER_SIZE) {
    flush();
  } else {
    scheduleFlush();
  }
}

/**
 * 初始化 Web Vitals 采集。
 * web-vitals 静态导入使浏览器 Effect runtime 成为唯一异步终端。
 * 仅在客户端调用。
 */
export function initWebVitals() {
  if (typeof window === "undefined" || initialized) {
    return;
  }
  initialized = true;

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      flush();
    }
  });

  onCLS(onMetric);
  onFCP(onMetric);
  onINP(onMetric);
  onLCP(onMetric);
  onTTFB(onMetric);
}
